import api from '@/api';
import type {
    UserData as ApiUserData,
    AuthResponse,
    LoginCredentials,
    RegistrationData,
    ResetPasswordData,
} from '@/types/api';
import { Preferences } from '@capacitor/preferences';
import type { AxiosResponse } from 'axios';
import { ApiFactory } from './base/ApiFactory';

export interface UserData {
    id: string;
    customerId?: string;
    email: string;
    firstName: string;
    lastName: string;
    mobileNumber: string;
    completeProfile: boolean;
    birthdate?: string;
    privileges?: any;
    profilePicture?: string;
    image?: string;
    loyaltyMemberId?: string | null;
    pointsBalance?: number;
    activeMembership?: boolean;
    hasActiveMembership?: boolean;
    hasReferralCode?: boolean;
    referralCode?: string | null;
    tier?: {
        value: number;
        title: string;
        color: string;
        description: string;
    };
    status?: {
        id: number;
        text: string;
        color: string;
    };
    gender?: string | null;
    facebookLink?: string | null;
    referralYearStart?: string | null;
    referralYearEnd?: string | null;
    engineNo?: string | null;
    invoiceNo?: string | null;
}

export class AuthService extends ApiFactory<ApiUserData> {
    private static readonly TOKEN_KEY = 'auth_token';
    private static readonly USER_KEY = 'auth_user';
    private static readonly TOKEN_EXPIRY = 'auth_expiry';
    private static readonly TOKEN_REFRESH_MARGIN = 300000; // 5 minutes before expiry
    private static readonly SHORT_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours
    private static readonly LONG_EXPIRY = 30 * 24 * 60 * 60 * 1000; // 30 days

    constructor() {
        super('/auth', api);
    }

    async login(credentials: LoginCredentials): Promise<AuthResponse> {
        try {
            const response: AxiosResponse<AuthResponse> = await api.post(
                '/auth/session/login',
                credentials,
            );
            await this.setSession(response.data);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async register(userData: RegistrationData): Promise<AuthResponse> {
        try {
            const response: AxiosResponse<AuthResponse> = await api.post(
                '/auth/register/validate',
                userData,
            );
            await this.setSession(response.data);
            return response.data;
        } catch (error: any) {
            throw error;
        }
    }

    async resendOTP(email: string): Promise<void> {
        try {
            await api.post(`/auth/register/${email}/resend-otp`);
        } catch (error) {
            throw error;
        }
    }

    async validateOTP(email: string, otp: string): Promise<AuthResponse> {
        try {
            const response: AxiosResponse<AuthResponse> = await api.post(
                `/auth/register/verify-otp/`,
                {
                    otp: otp,
                    email: email,
                },
            );
            await this.setSession(response.data);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async validatePassword(
        email: string,
        password: string,
        password_confirmation: string,
    ): Promise<AuthResponse> {
        const response: AxiosResponse<AuthResponse> = await api.post(
            '/auth/register/set-password',
            {
                email,
                password,
                password_confirmation,
            },
        );
        await this.setSession(response.data);
        return response.data;
    }

    async logout(): Promise<void> {
        try {
            await api.delete('/auth/session/logout');
        } finally {
            await this.clearSession();
        }
    }

    // check token validity
    async validateCurrentToken(): Promise<boolean> {
        const token = await this.getToken();
        if (!token) return false;

        const { value: expiryStr } = await Preferences.get({
            key: AuthService.TOKEN_EXPIRY,
        });

        if (!expiryStr) return false;
        return Date.now() < parseInt(expiryStr);
    }

    async setSession(authResponse: AuthResponse): Promise<void> {
        const expiryDuration = AuthService.LONG_EXPIRY;
        const expiresAt = Date.now() + expiryDuration;

        await Promise.all([
            Preferences.set({
                key: AuthService.TOKEN_KEY,
                value: authResponse.token,
            }),
            Preferences.set({
                key: AuthService.USER_KEY,
                value: JSON.stringify(authResponse.data),
            }),
            Preferences.set({
                key: AuthService.TOKEN_EXPIRY,
                value: expiresAt.toString(),
            }),
        ]);

        api.defaults.headers.common.Authorization = `Bearer ${authResponse.token}`;
    }

    async clearSession(): Promise<void> {
        await Promise.all([
            Preferences.remove({ key: AuthService.TOKEN_KEY }),
            Preferences.remove({ key: AuthService.USER_KEY }),
            Preferences.remove({ key: AuthService.TOKEN_EXPIRY }),
        ]);
        delete api.defaults.headers.common.Authorization;
    }

    async getCurrentUser(): Promise<UserData | null> {
        const { value } = await Preferences.get({ key: AuthService.USER_KEY });
        return value ? JSON.parse(value) : null;
    }

    async updateStoredUser(user: UserData): Promise<void> {
        await Preferences.set({
            key: AuthService.USER_KEY,
            value: JSON.stringify(user),
        });
    }

    async getToken(): Promise<string | null> {
        const { value } = await Preferences.get({ key: AuthService.TOKEN_KEY });
        return value;
    }

    async isAuthenticated(): Promise<boolean> {
        const token = await this.getToken();
        if (!token) return false;

        const { value: expiryStr } = await Preferences.get({
            key: AuthService.TOKEN_EXPIRY,
        });

        if (!expiryStr) return false;
        const expiry = parseInt(expiryStr);
        return Date.now() < expiry - AuthService.TOKEN_REFRESH_MARGIN;
    }

    /**
     * Confirms registration OTP.
     * @param otp - The OTP code to confirm
     * @returns Promise with auth response
     */
    async confirmRegistrationOtp(otp: string): Promise<AuthResponse> {
        const response = await this.post<AuthResponse>(`${this.resource}/register/verify-otp/`);
        await this.setSession(response);
        return response;
    }

    /**
     * Resends registration OTP.
     * @returns Promise that resolves when OTP is resent
     */
    async resendRegistrationOtp(email: string, channel?: string): Promise<void> {
        await this.post<void>(`${this.resource}/register/resend-otp`, { email, ...(channel && { channel }) });
    }

    async resendValidateWithChannel(data: Record<string, any>): Promise<void> {
        await api.post('/auth/register/validate', data);
    }

    async forgotPassword(email: string, channel?: string): Promise<void> {
        await this.post<void>(`${this.resource}/forgot-password/validate`, { email, ...(channel && { channel }) });
    }

    async resendPasswordOtp(email: string, channel?: string): Promise<void> {
        await this.post<void>(`${this.resource}/forgot-password/resend-otp`, { email, ...(channel && { channel }) });
    }

    /**
     * Confirms password reset OTP.
     * @param email - User's email address
     * @param data - Object containing the OTP code
     * @returns Promise that resolves when OTP is confirmed
     */
    async confirmPasswordOtp(email: string, data: { otp: string }): Promise<void> {
        await this.post<void>(`${this.resource}/forgot-password/verify-otp/`, {
            email: email,
            otp: data.otp,
        });
    }
    async validatePasswordOtp(email: string, data: { otp: string }): Promise<void> {
        await this.post<void>(`${this.resource}/forgot-password/verify-otp/`);
    }

    /**
     * Resets user password.
     * @param email - User's email address
     * @param data - New password data
     * @returns Promise that resolves when password is reset
     */
    async resetPassword(email: string, data: ResetPasswordData): Promise<void> {
        await this.post<void>(`${this.resource}/forgot-password/reset`, { ...data, email });
    }

    /**
     * Login with Google OAuth
     * @param idToken - Google ID token from OAuth flow
     * @returns Promise with auth response
     */
    async loginWithGoogle(idToken: string): Promise<AuthResponse> {
        try {
            const response: AxiosResponse<AuthResponse> = await api.post('/auth/social/google', {
                id_token: idToken,
            });
            await this.setSession(response.data);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Login with Facebook OAuth
     * @param accessToken - Facebook access token from OAuth flow
     * @returns Promise with auth response
     */
    async loginWithFacebook(accessToken: string): Promise<AuthResponse> {
        try {
            const response: AxiosResponse<AuthResponse> = await api.post('/auth/social/facebook', {
                access_token: accessToken,
            });
            await this.setSession(response.data);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Fetches the current user data from the server.
     * @returns Promise with the user data response
     */
    async fetchCurrentUser(): Promise<AuthResponse> {
        const response: AxiosResponse<AuthResponse> = await api.get('/auth/user');
        return response.data;
    }

    /**
     * Checks if the user is authorized and returns user data.
     * @returns Promise with authorized user data
     */
    async checkAuthorized(): Promise<UserData> {
        const response = await this.get<{ data: UserData }>(`${this.resource}/session/authorized`);
        return response.data;
    }

    /**
     * Refreshes user data from the API and updates stored user data.
     * @returns Promise with updated user data
     */
    async refreshAndUpdateUser(): Promise<UserData> {
        const userData = await this.checkAuthorized();
        await this.updateStoredUser(userData);
        return userData;
    }
}

export class AuthError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'AuthError';
    }
}

export const authService = new AuthService();
